package br.com.texeratx.boardgametimeapp.ui.screens.games

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.FilterList
import androidx.compose.material.icons.filled.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Badge
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.ModalBottomSheet
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TextField
import androidx.compose.material3.TextFieldDefaults
import androidx.compose.material3.TopAppBar
import androidx.compose.material3.TopAppBarDefaults
import androidx.compose.material3.TopAppBarScrollBehavior
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.nestedscroll.nestedScroll
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.ui.components.DropdownSelector
import br.com.texeratx.boardgametimeapp.ui.components.GameThumb
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppImagePlaceholder
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppLoadingOverlay
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppSectionCard
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.screens.games.create.CreateGameScreen
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailScreen
import br.com.texeratx.boardgametimeapp.ui.screens.games.model.GameUI
import cafe.adriel.voyager.navigator.currentOrThrow

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GamesUI(
    state: GamesUiState,
    onSortChange: (GameSortOption) -> Unit,
    onDismissError: () -> Unit,
    onSearch: (String) -> Unit,
    onApplyFilters: (GamesFilter) -> Unit,
) {
    val S = LocalStrings.current
    val navigator = cafe.adriel.voyager.navigator.LocalNavigator.currentOrThrow
    val scrollBehavior = TopAppBarDefaults.enterAlwaysScrollBehavior()
    var showFilters by remember { mutableStateOf(false) }


    AppScreenScaffold(
        modifier = Modifier.fillMaxSize(),
        floatingActionButton = {
            androidx.compose.material3.FloatingActionButton(
                onClick = {
                    navigator.push(CreateGameScreen())
                }
            ) {
                Icon(
                    imageVector = Icons.Default.Add,
                    contentDescription = S.addGame
                )
            }
        }
    ) { padding ->

        Column(
            modifier = Modifier
            .fillMaxSize()
            .padding(padding)
            .nestedScroll(scrollBehavior.nestedScrollConnection)
        ) {

            GamesTopBar(
                selected = state.sort,
                onSortChange = onSortChange,
                games = state.games,
                onBack = { navigator.pop() },
                scrollBehavior = scrollBehavior,
                currentQuery = state.searchQuery,
                onSearch = onSearch,
                onOpenFilters = { showFilters = true },
            )

            if (
                state.filters.category != null ||
                state.filters.mode != null ||
                state.filters.players != null
            ) {
                ActiveFiltersChips(
                    filters = state.filters,
                    onRemoveCategory = {
                        onApplyFilters(state.filters.copy(category = null))
                    },
                    onRemoveMode = {
                        onApplyFilters(state.filters.copy(mode = null))
                    },
                    onRemovePlayers = {
                        onApplyFilters(state.filters.copy(players = null))
                    }
                )
            }


            Spacer(Modifier.height(8.dp))

            when {
                state.isLoading -> Unit // overlay já cobre
                state.games.isEmpty() -> {
                    GamesEmptyState(
                        message = if (state.searchQuery.isNotBlank())
                            S.noGamesFoundFor(state.searchQuery)
                        else
                            S.noGamesYet
                    )
                }
                else -> {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        verticalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        items(state.games) { game ->
                            GameItem(game)
                        }
                    }
                }
            }

        }

        // ------------------------
        // LOADING OVERLAY
        // ------------------------
        AppLoadingOverlay(isVisible = state.isLoading)

        // ------------------------
        // ERROR DIALOG
        // ------------------------
        state.error?.let { message ->
            AlertDialog(
                onDismissRequest = onDismissError,
                title = { Text(S.errorLoading) },
                text = { Text(message) },
                confirmButton = {
                    TextButton(onClick = onDismissError) { Text(S.ok) }
                }
            )
        }

        if (showFilters) {
            GamesFilterBottomSheet(
                current = state.filters,
                categories = state.availableCategories,
                modes = state.availableModes,
                onApply = {
                    onApplyFilters(it)
                    showFilters = false
                },
                onDismiss = { showFilters = false }
            )
        }

    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GamesTopBar(
    selected: GameSortOption,
    onSortChange: (GameSortOption) -> Unit,
    games: List<GameUI>,
    onBack: () -> Unit,
    scrollBehavior: TopAppBarScrollBehavior,
    currentQuery: String,
    onSearch: (String) -> Unit,
    onOpenFilters: () -> Unit
) {
    val S = LocalStrings.current
    var expanded by remember { mutableStateOf(false) }
    var isSearching by remember { mutableStateOf(false) }
    var query by remember { mutableStateOf("") }


    TopAppBar(
        scrollBehavior = scrollBehavior,
        title = {
            if (isSearching) {
                TextField(
                    value = currentQuery,
                    onValueChange = onSearch,
                    placeholder = { Text(S.searchGame) },
                    singleLine = true,
                    modifier = Modifier.fillMaxWidth(),
                    colors = TextFieldDefaults.colors(
                        focusedContainerColor = Color.Transparent,
                        unfocusedContainerColor = Color.Transparent,
                        focusedIndicatorColor = Color.Transparent,
                        unfocusedIndicatorColor = Color.Transparent
                    )
                )
            } else {
                Row(
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = S.navGames,
                        style = MaterialTheme.typography.titleLarge,
                        fontWeight = FontWeight.Bold
                    )

                    Spacer(Modifier.width(8.dp))

                    Badge(
                        containerColor = MaterialTheme.colorScheme.primary,
                        contentColor = MaterialTheme.colorScheme.onPrimary
                    ) {
                        Text(games.size.toString())
                    }
                }

            }
        },

        // 🔙 BOTÃO VOLTAR
        navigationIcon = {
            Icon(
                imageVector = Icons.Default.ArrowBack,
                contentDescription = S.back,
                modifier = Modifier
                    .padding(start = 12.dp)
                    .clickable { onBack() }
            )
        },

        // 🎨 CORES DA TOP BAR
        colors = AppTopBarColors(),

        actions = {
            Icon(
                imageVector = if (isSearching) Icons.Default.Close else Icons.Default.Search,
                contentDescription = S.searchGame,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable {
                        isSearching = !isSearching
                        if (!isSearching) query = ""
                    }
            )

            Icon(
                imageVector = Icons.Default.FilterList,
                contentDescription = S.filtersTitle,
                modifier = Modifier
                    .padding(horizontal = 8.dp)
                    .clickable { onOpenFilters() }
            )

            Row(
                modifier = Modifier
                    .padding(end = 12.dp)
                    .clip(RoundedCornerShape(12.dp))
                    .clickable { expanded = true }
                    .background(
                        MaterialTheme.colorScheme.primary.copy(alpha = 0.15f)
                    )
                    .padding(horizontal = 10.dp, vertical = 6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {

                Text(
                    selected.label,
                    fontWeight = FontWeight.SemiBold
                )

                Spacer(Modifier.width(4.dp))

                Icon(
                    imageVector = Icons.Default.ArrowDropDown,
                    contentDescription = S.sortAlphabetical
                )
            }

            DropdownMenu(
                expanded = expanded,
                onDismissRequest = { expanded = false }
            ) {

                DropdownMenuItem(
                    text = { Text(S.sortAlphabetical) },
                    onClick = {
                        expanded = false
                        onSortChange(GameSortOption.ALPHABETICAL)
                    }
                )

                DropdownMenuItem(
                    text = { Text(S.sortMostPlayed) },
                    onClick = {
                        expanded = false
                        onSortChange(GameSortOption.MOST_PLAYED)
                    }
                )
            }
        }
    )
}

@Composable
fun GameItem(game: GameUI) {
    val S = LocalStrings.current
    val navigator = cafe.adriel.voyager.navigator.LocalNavigator.currentOrThrow

    AppSectionCard(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 12.dp),
        shape = RoundedCornerShape(16.dp),
        contentPadding = PaddingValues(12.dp),
        onClick = { navigator.push(GameDetailScreen(game.id)) },
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth(),
            horizontalArrangement = Arrangement.Start
        ) {

            // -------------------------------
            // IMAGEM DO JOGO (ESQUERDA)
            // -------------------------------
            if (!game.imageUrl.isNullOrBlank()) {
                GameThumb(
                    imageUrl = game.imageUrl,
                    contentDescription = game.name,
                    modifier = Modifier
                        .width(90.dp)
                        .height(120.dp)
                        .clip(RoundedCornerShape(12.dp))
                )
            } else {
                AppImagePlaceholder(
                    text = S.noImage,
                    modifier = Modifier
                        .width(90.dp)
                        .height(120.dp),
                    shape = RoundedCornerShape(12.dp),
                )
            }

            Spacer(Modifier.width(12.dp))

            // -------------------------------
            // INFORMAÇÕES (DIREITA)
            // -------------------------------
            Column(
                modifier = Modifier.weight(1f)
            ) {

                // Nome
                Text(
                    game.name,
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )

                Spacer(Modifier.height(6.dp))

                // Jogadores
                Text(S.gamePlayersRange((game.minPlayers?.toString() ?: "?"), (game.maxPlayers?.toString() ?: "?")))

                // Duração
                Text(S.gameDurationFormat((game.duration?.toString() ?: "?")))

                // Partidas jogadas
                Text(
                    S.gameMatchesPlayedFormat(game.matchesPlayed),
                    color = MaterialTheme.colorScheme.primary
                )

                // Categoria
                Text(S.gameCategoryFormat(game.category ?: "-"))
            }
        }
    }
}

@Composable
fun GamesEmptyState(
    modifier: Modifier = Modifier,
    message: String
) {
    Box(
        modifier = modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                text = "🎲",
                style = MaterialTheme.typography.displayMedium
            )

            Spacer(Modifier.height(12.dp))

            Text(
                text = message,
                style = MaterialTheme.typography.bodyLarge,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GamesFilterBottomSheet(
    current: GamesFilter,
    categories: List<String>,
    modes: List<String>,
    onApply: (GamesFilter) -> Unit,
    onDismiss: () -> Unit
) {
    val S = LocalStrings.current
    var category by remember { mutableStateOf(current.category) }
    var mode by remember { mutableStateOf(current.mode) }
    var players by remember { mutableStateOf(current.players) }

    ModalBottomSheet(onDismissRequest = onDismiss) {
        Column(Modifier.padding(16.dp)) {

            Text(S.filtersTitle, style = MaterialTheme.typography.titleLarge)
            Spacer(Modifier.height(16.dp))

            // Categoria
            DropdownSelector(
                label = S.labelCategory,
                options = categories,
                selected = category,
                onSelect = { category = it }
            )

            // Modo
            DropdownSelector(
                label = S.labelMode,
                options = modes,
                selected = mode,
                onSelect = { mode = it }
            )

            // Jogadores
            TextField(
                value = players?.toString() ?: "",
                onValueChange = { players = it.toIntOrNull() },
                label = { Text(S.labelPlayerCount) },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number)
            )

            Spacer(Modifier.height(24.dp))

            Button(
                modifier = Modifier.fillMaxWidth(),
                onClick = {
                    onApply(
                        GamesFilter(
                            category = category,
                            mode = mode,
                            players = players
                        )
                    )
                }
            ) {
                Text(S.applyFilters)
            }

            Spacer(Modifier.height(12.dp))

            TextButton(
                modifier = Modifier.fillMaxWidth(),
                onClick = {
                    onApply(GamesFilter()) // limpa tudo
                }
            ) {
                Text(S.clearFilters)
            }
        }
    }
}

@Composable
fun ActiveFiltersChips(
    filters: GamesFilter,
    onRemoveCategory: () -> Unit,
    onRemoveMode: () -> Unit,
    onRemovePlayers: () -> Unit
) {
    val S = LocalStrings.current
    Row(
        modifier = Modifier
            .padding(horizontal = 12.dp, vertical = 8.dp),
        horizontalArrangement = Arrangement.spacedBy(8.dp)
    ) {

        filters.category?.let {
            FilterChip(
                label = S.filterCategoryFormat(it),
                onRemove = onRemoveCategory
            )
        }

        filters.mode?.let {
            FilterChip(
                label = S.filterModeFormat(it),
                onRemove = onRemoveMode
            )
        }

        filters.players?.let {
            FilterChip(
                label = S.filterPlayersFormat(it.toString()),
                onRemove = onRemovePlayers
            )
        }
    }
}

@Composable
fun FilterChip(
    label: String,
    onRemove: () -> Unit
) {
    val S = LocalStrings.current
    Row(
        modifier = Modifier
            .clip(RoundedCornerShape(16.dp))
            .background(MaterialTheme.colorScheme.secondaryContainer)
            .padding(horizontal = 12.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = MaterialTheme.typography.labelMedium
        )

        Spacer(Modifier.width(6.dp))

        Icon(
            imageVector = Icons.Default.Close,
            contentDescription = S.remove,
            modifier = Modifier
                .size(16.dp)
                .clickable { onRemove() }
        )
    }
}





